import React, {useState} from 'react'
import {useIntl, FormattedMessage} from 'react-intl'
import {HStack, Stack, StackProps, useMultiStyleConfig, Text, TextProps, Box, Link} from '@chakra-ui/react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useForm} from 'react-hook-form'
import {Button} from '_uiComponents'
import {useResetPassword} from '_customHooks'

// Hooks
import {FormFactory, FormValues} from '_factories'

interface ResetPasswordDrawerProps {
    phonelogin?: boolean
    registerUrl?: string
}

interface ResetPasswordResponse {
    success: boolean
    receivedMsgHeading?: string
    receivedMsgBody?: string
    returnUrl?: string
    btnText?: string
    errorMsg?: string
    loginEmail?: string
}

/**
 * @name ResetPasswordDrawer
 *
 * @component
 *
 * @description
 * Renders reset password drawer.

 *
 * @param {phonelogin} props - enalbe phone login
 * @param {registerUrl} props.registerUrl - URL for registration page
 *
 * @returns {JSX.Element} Rendered `NewsletterModal` component.
 */

const ResetPasswordDrawer = ({phonelogin, registerUrl}: ResetPasswordDrawerProps): JSX.Element => {
    const styles = useMultiStyleConfig('ResetPasswordDrawer') as Record<string, StackProps & TextProps>
    const {locale} = useMultiSite()
    const {formatMessage} = useIntl()
    const {executeResetPasswordFlow} = useResetPassword(locale.id)
    const [resetResponse, setResetResponse] = useState<ResetPasswordResponse>({success: false})

    const {
        control,
        formState: {errors},
        handleSubmit,
        setError,
    } = useForm<FormValues>()

    // const validateEmail = (email) => {
    //     const regex = /^[\w.%+\-]+@[\w.\-]+\.[\w]{2,6}$/
    //     return regex.test(email)
    // }

    const resetPasswordForm: Array<{
        type: string
        name: keyof FormValues
        label: string
        placeholder?: string
        options?: {value: string; label: string}[]
        defaultValue?: string
    }> = [
        phonelogin
            ? {
                  type: 'text',
                  name: 'email-phone',
                  label: formatMessage({id: 'label.input.login.emailandphone', defaultMessage: 'Enter an e-mail or a phone number'}),
              }
            : {
                  type: 'email',
                  name: 'email',
                  label: formatMessage({id: 'label.input.login.email', defaultMessage: 'Enter e-mail'}),
              },
    ]
    const submitResetPassword = async (data: FormValues) => {
        try {
            const loginEmail = data.email || data['email-phone']
            const response = await executeResetPasswordFlow(loginEmail)
            setResetResponse(response)

            if (response.success === false && response.loginEmail) {
                setError(phonelogin ? 'email-phone' : 'email', {type: 'manual', message: response.loginEmail})
            }
        } catch {
            setResetResponse({
                success: false,
            })
        }
    }

    return (
        <>
            <Stack
                {...styles.resetPasswordBox}
                data-testid="reset-password-drawer"
                as="form"
                noValidate
                onSubmit={handleSubmit((data) => submitResetPassword(data))}
            >
                {resetResponse.success === true ? (
                    <>
                        <Stack {...styles.resetPasswordSuccessBox}>
                            <Text {...styles.succesTitle}>{resetResponse.receivedMsgHeading || ''}</Text>

                            <Text {...styles.succesmessage}>{resetResponse.receivedMsgBody || ''}</Text>

                            <Button variant="primary" as={Link} href={resetResponse.returnUrl || ''}>
                                {resetResponse.btnText || ''}
                            </Button>
                        </Stack>
                    </>
                ) : (
                    <>
                        <HStack {...styles.resetPasswordtitle}>
                            <Text {...styles.title}>
                                <FormattedMessage
                                    id="label.resetPassword.subtitle"
                                    defaultMessage="Enter the e-mail associated with your account and you will receive instructions to reset your password."
                                />
                            </Text>
                        </HStack>
                        {resetPasswordForm.map((schema, index) => (
                            <FormFactory
                                key={`${index}_${schema.name}`}
                                control={control}
                                errors={errors}
                                name={schema.name}
                                type={schema.type}
                                label={schema.label}
                                placeholder={schema.placeholder}
                                options={schema.options}
                                defaultValue={schema.defaultValue}
                            />
                        ))}
                        <Box {...styles.confirmButton}>
                            <Button variant="primary" type="submit" data-testid="reset-password-button">
                                <FormattedMessage id={'label.resetPassword.sand'} defaultMessage="CONFIRM" />
                            </Button>
                        </Box>
                    </>
                )}
            </Stack>
            <Stack {...styles.registrationBox}>
                <Text {...styles.registrationTitle} data-testid="registration-title">
                    <FormattedMessage id="registration.offcanvas.title" defaultMessage="Create an account" />
                </Text>

                <Text {...styles.registrationText}>
                    <FormattedMessage
                        id="registration.offcanvas.text"
                        defaultMessage="Enjoy a personalized experience and discover all the exclusive services."
                    />
                </Text>

                <Button variant="primary" type="button" as={Link} href={registerUrl} data-testid="registration-button">
                    <FormattedMessage id="offCanvas.registration.button" defaultMessage="REGISTER" />
                </Button>
            </Stack>
        </>
    )
}

export {ResetPasswordDrawer}
